import { SharedWith } from "src/apps/AppFlow/pages";
import PropertyInfo from "src/apps/AppFlow/screens/root/Layout/sections/PropertyAndLoanInformation/pages/PropertyInfo";
import { Page } from "src/components/FlowLayout";
import { PageStateConverter } from "src/hooks/usePageState";
import { Applicant, OtherOwnedProperty } from "src/types/Applicant";

import { enums, formatters } from "@fraction/shared";

import MortgageInformation from "../MortgageInformation/Overview";
import { HomeLocation, HomeValue, PropertyPlans, RentalIncome } from "./pages";

export type OtherOwnedPropertiesPagesArgs = PageStateConverter<
  OtherOwnedProperty,
  | "propertyLocation"
  | "propertyValue"
  | "propertyType"
  | "rentalIncome"
  | "planToSellDuringProcess"
  | "mortgages"
  | "owners"
> & {
  applicant: Applicant | undefined;
  otherApplicants: Applicant[] | undefined;
  onComplete: () => void;
};

const OtherOwnedPropertiesPages = ({
  propertyLocation,
  propertyValue,
  propertyType,
  rentalIncome,
  planToSellDuringProcess,
  mortgages,
  owners,
  applicant,
  otherApplicants,
}: OtherOwnedPropertiesPagesArgs): Page[] => {
  const propertyAddress = propertyLocation?.value
    ? formatters.property.shortFormattedAddress(propertyLocation?.value)
    : undefined;

  return [
    {
      name: "otherOwnedProperties_homeLocation",
      component: (
        <HomeLocation
          secondaryProperty
          title="What's the address of your other property?"
          description="This property doesn't have to be in a region Fraction serves - it's just for our records."
          {...propertyLocation}
        />
      ),
    },
    otherApplicants?.length &&
      applicant && {
        name: "otherOwnedProperties_owners",
        component: (
          <SharedWith
            applicant={applicant}
            otherApplicants={otherApplicants || []}
            sharedItemName={
              propertyLocation.value ? formatters.property.shortFormattedAddress(propertyLocation.value) : ""
            }
            isSubpage
            {...owners}
          />
        ),
      },
    {
      name: "otherOwnedProperties_homeValue",
      component: <HomeValue propertyAddress={propertyAddress} minHomeValue={0} {...propertyValue} />,
    },
    {
      name: "otherOwnedProperties_propertyInfo",
      component: <PropertyInfo propertyAddress={propertyAddress} {...propertyType} />,
    },
    propertyType?.value?.propertyUsage === enums.PropertyUsage.INVESTMENT && {
      name: "otherOwnedProperties_rentalIncome",
      component: <RentalIncome propertyAddress={propertyAddress} isSubpage {...rentalIncome} />,
    },
    {
      name: "otherOwnedProperties_propertyPlans",
      component: <PropertyPlans propertyAddress={propertyAddress} {...planToSellDuringProcess} />,
    },
    {
      name: "otherOwnedProperties_mortgages",
      component: <MortgageInformation {...mortgages} simple propertyAddress={propertyAddress} isSubpage />,
    },
  ].filter((p): p is Page => Boolean(p));
};

export default OtherOwnedPropertiesPages;
